import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { currentBranchId } from '../config/branchContext';

const SESSION_KEY = 'sushikiug:qr_session_id';
const SCAN_LOGGED_KEY = 'sushikiug:qr_scan_logged';

export type ScanSource = 'web' | 'table';

export interface ScanContext {
  brand: 'sushiki' | 'donvito';
  source: ScanSource;
  tableNumber: number | null;
  sessionId: string;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'qrs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = uuid();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return uuid();
  }
}

export function detectScanContext(): ScanContext {
  const sessionId = getOrCreateSessionId();
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const brand: 'sushiki' | 'donvito' = host.includes('donvito') ? 'donvito' : 'sushiki';

  let source: ScanSource = 'web';
  let tableNumber: number | null = null;

  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    const tableMatch = path.match(/\/table\/(\d+)/i);
    if (tableMatch) {
      source = 'table';
      tableNumber = parseInt(tableMatch[1], 10);
    }
  }

  return { brand, source, tableNumber, sessionId };
}

export async function logScanIfFirstVisit(): Promise<void> {
  if (typeof window === 'undefined') return;

  let alreadyLogged = false;
  try {
    alreadyLogged = sessionStorage.getItem(SCAN_LOGGED_KEY) === '1';
  } catch {
    // sessionStorage unavailable — best-effort, log anyway
  }
  if (alreadyLogged) return;

  const ctx = detectScanContext();

  try {
    sessionStorage.setItem(SCAN_LOGGED_KEY, '1');
  } catch {
    // ignore
  }

  try {
    await addDoc(collection(db, 'qr_scans'), {
      brand: ctx.brand,
      branch_id: currentBranchId(),
      source: ctx.source,
      table_number: ctx.tableNumber,
      session_id: ctx.sessionId,
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
      language: navigator.language || null,
      viewport_w: window.innerWidth || null,
      viewport_h: window.innerHeight || null,
      ts: serverTimestamp(),
      converted: false,
    });
  } catch (err) {
    // Soft fail — analytics must never break the customer flow
    try {
      sessionStorage.removeItem(SCAN_LOGGED_KEY);
    } catch {
      // ignore
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[scanTracker] failed to log scan', err);
    }
  }
}
